import React from 'react';
import { useTranslation } from 'react-i18next';
import { BodyText, Checkbox, Skeleton, Table } from '@gaiads/telia-react-component-library';
import { BundlePrice, LocalBundleEnvelope, LocaleResponse } from '@cdaas/api-clients/dist/product-service-openapi';

import { SalesChannelBundleListItemType } from '../../../store/adminBundles';
import BundleListItem from './BundleListItem';
import MainListItem from './MainListItem';
import VariantListItem from './VariantListItem';

import './Bundles.scss';

interface RenderVariantItemsProps {
  itemContent: LocalBundleEnvelope | undefined;
  isOpen: boolean;
  isLoaded: boolean;
}
const RenderVariantItems = ({ itemContent, isOpen, isLoaded }: RenderVariantItemsProps) => {
  if (isOpen && !isLoaded) {
    return (
      <Table.Row className="daas-product-table__skeleton-row">
        <Table.Cell colSpan={10}>
          <Skeleton widthInPercentages={100} height={88} />
        </Table.Cell>
      </Table.Row>
    );
  } else if (isOpen && isLoaded && itemContent) {
    const { main_item, items } = itemContent.bundle;
    return (
      <>
        {main_item.variants?.map(variant => <MainListItem key={`main-item-${variant.id}`} item={variant} />)}
        {items.map(addonGroup =>
          addonGroup.options.map(option => <VariantListItem key={`addon-${addonGroup.id}`} item={addonGroup} option={option} />),
        )}
      </>
    );
  } else {
    return <></>;
  }
};

type TableHeader = string | undefined;
type BundleTableHeaders = Array<TableHeader>;

export interface BundlesListProps {
  bundleListItems: SalesChannelBundleListItemType[];
  locales: LocaleResponse[];
  selectAll: boolean;
  currency?: string;
  onBundleChecked: (bundleId: number) => void;
  onAllChecked: () => void;
  onClickRow: (bundleId: number) => void;
  isSelected: (bundleId: number) => boolean;
  onOpenRow: (bundleId: number) => void;
  isOpened: (bundleId: number) => boolean;
  isLoaded: (bundleId: number) => boolean;
  handlePriceSave: (bundleId: number, updatedBundlePrice: BundlePrice) => Promise<void>;
  salesChannelUsesDynamicPricing: boolean;
}

const BundlesList = (props: BundlesListProps) => {
  const {
    locales,
    selectAll,
    bundleListItems,
    currency,
    onAllChecked,
    onBundleChecked,
    onClickRow,
    isSelected,
    onOpenRow,
    isOpened,
    isLoaded,
    handlePriceSave,
    salesChannelUsesDynamicPricing,
  } = props;

  const { t } = useTranslation('bundlesview');
  const tableHeaders: BundleTableHeaders = [
    undefined,
    undefined,
    t('bundles_table.bundleBrand'),
    t('bundles_table.bundle_name'),
    t('bundles_table.availability'),
    t('bundles_table.languages'),
    t('bundles_table.insurance'),
    t('bundles_table.price_type'),
    currency ? t('bundles_table.price_with_currency', { currency }) : t('bundles_table.price'),
    salesChannelUsesDynamicPricing ? t('bundles_table.price_updated') : t('bundles_table.vat'),
  ];
  if (!salesChannelUsesDynamicPricing) {
    tableHeaders.splice(6, 1);
    tableHeaders.push(undefined);
  }

  return (
    <Table className={'products-table'}>
      <thead>
        <Table.Row>
          <Table.HeaderCell type="checkbox">
            <Checkbox
              checked={selectAll}
              onChange={() => {
                onAllChecked();
              }}
            />
          </Table.HeaderCell>
          {tableHeaders.map((header, index) => (
            <Table.HeaderCell key={`table-header-${index}`}>
              <BodyText fontWeight="bold">{header}</BodyText>
            </Table.HeaderCell>
          ))}
        </Table.Row>
      </thead>
      <tbody>
        {bundleListItems.map((item: SalesChannelBundleListItemType, index) => (
          <BundleListItem
            key={index}
            item={item}
            locales={locales}
            open={isOpened(item.id)}
            onItemChecked={onBundleChecked}
            onOpenRow={onOpenRow}
            onRowClick={onClickRow}
            selected={isSelected(item.id)}
            handlePriceSave={handlePriceSave}
            salesChannelUsesDynamicPricing={salesChannelUsesDynamicPricing}
          >
            <RenderVariantItems itemContent={item.content} isOpen={isOpened(item.id)} isLoaded={isLoaded(item.id)} />
          </BundleListItem>
        ))}
      </tbody>
    </Table>
  );
};

export default BundlesList;
